import {
    BUSINESS_EVENTBRITE_CATEGORY,
    FOOD_AND_DRINK_EVENTBRITE_CATEGORY,
    HEALTH_EVENTBRITE_CATEGORY,
    HOBBIES_EVENTBRITE_CATEGORY,
    MUSIC_EVENTBRITE_CATEGORY,
    NIGHTLIFE_EVENTBRITE_CATEGORY,
    PERFORMING_EVENTBRITE_CATEGORY,
    SEASONAL_EVENTBRITE_CATEGORY,
    SPORTS_EVENTBRITE_CATEGORY,
} from '@eventbrite/categories';
import {
    Arts,
    BuisnessProfession,
    FoodDrink,
    Health,
    Hobbies,
    Mic,
    Sports,
    Tickets,
} from '@eventbrite/eds-iconography';

import { DiscoBall, HolidayCalendar } from '@eventbrite/eds-iconography';
import { GenericLazyString, gettext } from '@eventbrite/i18n';
import React from 'react';
import { OrganicEventSearchFiltersApiPayload } from '../../../api/searchOrganicEvents';
import { bboxToUrlParam } from '../../../utils/location';
import { buildBBox } from '../utils';
export const WEEKEND_EVENTBRITE_CATEGORY = 'this_weekend';

/****************************** Home Page Category Carousel Constants ********************************/

export const EVENTBRITE_CATEGORY_CAROUSEL_ICON_MAP: {
    [key: string]: JSX.Element;
} = {
    [BUSINESS_EVENTBRITE_CATEGORY]: <BuisnessProfession viewBox="0 0 32 33" />,
    [MUSIC_EVENTBRITE_CATEGORY]: <Mic viewBox="0 0 40 41" />,
    [PERFORMING_EVENTBRITE_CATEGORY]: <Arts viewBox="0 0 40 41" />,
    [SEASONAL_EVENTBRITE_CATEGORY]: <HolidayCalendar viewBox="0 0 28 36" />,
    [FOOD_AND_DRINK_EVENTBRITE_CATEGORY]: <FoodDrink viewBox="0 0 40 41" />,
    [WEEKEND_EVENTBRITE_CATEGORY]: <Tickets viewBox="0 0 32 32" />,
    [HEALTH_EVENTBRITE_CATEGORY]: <Health viewBox="0 0 32 32" />,
    [HOBBIES_EVENTBRITE_CATEGORY]: <Hobbies viewBox="0 0 32 32" />,
    [SPORTS_EVENTBRITE_CATEGORY]: <Sports viewBox="0 0 32 32" />,
    [NIGHTLIFE_EVENTBRITE_CATEGORY]: <DiscoBall viewBox="0 0 47 48" />,
};

export const EVENTBRITE_CAROUSEL_CATEGORY_MAPPING = {
    [BUSINESS_EVENTBRITE_CATEGORY]: gettext('Business & Professional events'),
    [MUSIC_EVENTBRITE_CATEGORY]: gettext('Music events'),
    [PERFORMING_EVENTBRITE_CATEGORY]: gettext(
        'Visual & performing arts events',
    ),
    [FOOD_AND_DRINK_EVENTBRITE_CATEGORY]: gettext('Food & drinks events'),
    [WEEKEND_EVENTBRITE_CATEGORY]: gettext('Things to do this weekend 😎'),
};

const VIEW_MORE = gettext('View More');

/****************************** Home Page Category Carousel Constants ********************************/
export interface CATEGORY_CAROUSEL {
    key: string;
    name: GenericLazyString;
    icon: any;
    viewMore: GenericLazyString;
    eventSearch: OrganicEventSearchFiltersApiPayload;
}

export const HOME_PAGE_CATEGORY_CAROUSEL: CATEGORY_CAROUSEL[] = [
    {
        key: WEEKEND_EVENTBRITE_CATEGORY,
        name: EVENTBRITE_CAROUSEL_CATEGORY_MAPPING[WEEKEND_EVENTBRITE_CATEGORY],
        icon: EVENTBRITE_CATEGORY_CAROUSEL_ICON_MAP[
            WEEKEND_EVENTBRITE_CATEGORY
        ],
        viewMore: VIEW_MORE,
        eventSearch: {
            page_size: 6,
            dates: ['current_future', 'this_weekend'],
            aggs: {},
        },
    },
    {
        key: MUSIC_EVENTBRITE_CATEGORY,
        name: EVENTBRITE_CAROUSEL_CATEGORY_MAPPING[MUSIC_EVENTBRITE_CATEGORY],
        icon: EVENTBRITE_CATEGORY_CAROUSEL_ICON_MAP[MUSIC_EVENTBRITE_CATEGORY],
        viewMore: VIEW_MORE,
        eventSearch: {
            page_size: 6,
            tags: [MUSIC_EVENTBRITE_CATEGORY],
            aggs: {},
        },
    },
    {
        key: FOOD_AND_DRINK_EVENTBRITE_CATEGORY,
        name: EVENTBRITE_CAROUSEL_CATEGORY_MAPPING[
            FOOD_AND_DRINK_EVENTBRITE_CATEGORY
        ],
        icon: EVENTBRITE_CATEGORY_CAROUSEL_ICON_MAP[
            FOOD_AND_DRINK_EVENTBRITE_CATEGORY
        ],
        viewMore: VIEW_MORE,
        eventSearch: {
            page_size: 6,
            tags: [FOOD_AND_DRINK_EVENTBRITE_CATEGORY],
            aggs: {},
        },
    },
    {
        key: BUSINESS_EVENTBRITE_CATEGORY,
        name: EVENTBRITE_CAROUSEL_CATEGORY_MAPPING[
            BUSINESS_EVENTBRITE_CATEGORY
        ],
        icon: EVENTBRITE_CATEGORY_CAROUSEL_ICON_MAP[
            BUSINESS_EVENTBRITE_CATEGORY
        ],
        viewMore: VIEW_MORE,
        eventSearch: {
            page_size: 6,
            tags: [BUSINESS_EVENTBRITE_CATEGORY],
            aggs: {},
        },
    },
    {
        key: PERFORMING_EVENTBRITE_CATEGORY,
        name: EVENTBRITE_CAROUSEL_CATEGORY_MAPPING[
            PERFORMING_EVENTBRITE_CATEGORY
        ],
        icon: EVENTBRITE_CATEGORY_CAROUSEL_ICON_MAP[
            PERFORMING_EVENTBRITE_CATEGORY
        ],
        viewMore: VIEW_MORE,
        eventSearch: {
            page_size: 6,
            tags: [PERFORMING_EVENTBRITE_CATEGORY],
            aggs: {},
        },
    },
];

/****************************** Home Page Category Browse CTA Constants ********************************/

export const CATEGORY_BROWSE_KEYS = [
    MUSIC_EVENTBRITE_CATEGORY,
    NIGHTLIFE_EVENTBRITE_CATEGORY,
    PERFORMING_EVENTBRITE_CATEGORY,
    SEASONAL_EVENTBRITE_CATEGORY,
    HEALTH_EVENTBRITE_CATEGORY,
    HOBBIES_EVENTBRITE_CATEGORY,
    BUSINESS_EVENTBRITE_CATEGORY,
    FOOD_AND_DRINK_EVENTBRITE_CATEGORY,
];

interface seasonalModulelData {
    id: number;
    title: string;
    imgSrc: string;
    imgSrcMobile: string;
    bucketUrl: string;
    description: string;
}

export const SEASONAL_MODULE_DATA: { [key: string]: seasonalModulelData[] } = {
    SPD: [
        {
            id: 1,
            title: 'Bar Crawls',
            imgSrc: '',
            imgSrcMobile: '',
            bucketUrl: 'bar-crawls',
            description: '',
        },
        {
            id: 2,
            title: 'Parties',
            imgSrc: '',
            imgSrcMobile: '',
            bucketUrl: 'parties',
            description: '',
        },
        {
            id: 3,
            title: 'Festive fun',
            imgSrc: '',
            imgSrcMobile: '',
            bucketUrl: 'festive-fun',
            description: '',
        },
    ],
};

interface seasonalCarouselData {
    title: string | GenericLazyString;
    titleCSS: string;
    heapId: string;
    affCode: string;
    queryParams: any;
}

export const SEASONAL_CAROUSEL_DATA: { [key: string]: seasonalCarouselData } = {
    'Mardi Gras': {
        title: 'Mardi gras events 🎉',
        titleCSS: '',
        heapId: 'homepage-mardi-gras-carousel',
        affCode: 'ehometext',
        queryParams: {
            q: 'mardi',
            price: 'paid',
            page: 1,
            page_size: 40,
            aggs: {},
            dates: ['current_future'],
        },
    },
    SXSW: {
        title: "The best downtown events you don't need a badge for",
        titleCSS: 'wrap-title',
        heapId: 'homepage-sxsw-carousel',
        affCode: 'ehomefeatured',
        queryParams: {
            price: 'paid',
            page: 1,
            page_size: 40,
            aggs: {},
            dates: ['current_future'],
            bbox: bboxToUrlParam(
                buildBBox({ latitude: 30.271, longitude: -97.743 }),
            ),
        },
    },
};

// Lighthouse creator IDs for the top 10 metros in below order:-
// New York, London, Los Angeles, Washington DC, Chicago, Miami, Atlanta, Toronto, San Francisco, Melbourne
// Refer "packages/attendee/discover/src/__fixtures__/top10Cities.json" for Place ID to Name mapping
export const LIGHTHOUSE_ORGANIZERS: { [key: string]: string[] } = {
    '85977539': [
        '74868263803',
        '25882162729',
        '25462461241',
        '37912169403',
        '37912048563',
        '58010791773',
        '38734193573',
        '13964592640',
        '37243632173',
        '40007686053',
        '38937728413',
        '78788252983',
        '76504286353',
        '74773686363',
        '25383881003',
        '75279975753',
        '25383719883',
        '69927005433',
        '17062970520',
        '71574276173',
    ],
    '101750367': [
        '66236229593',
        '16861483918',
        '31057455955',
        '49749464473',
        '16086064715',
        '33493254017',
        '29723845575',
        '18091373298',
        '18439156860',
        '80501784323',
        '18213943324',
        '82970686503',
        '81334522973',
        '30844648659',
        '4527233341',
        '56165011123',
        '52696258643',
        '57695570563',
        '75877871933',
        '70072899593',
        '29914337751',
        '41135536123',
        '34116240609',
        '74311181333',
        '27345196777',
        '36463428713',
        '66293094883',
        '69790496513',
        '51404428693',
        '71633397143',
        '68995621063',
        '8714109745',
        '30032417564',
        '19748908389',
        '42484231363',
    ],
    '85923517': [
        '7971859108',
        '69671710793',
        '78738698513',
        '13644303199',
        '13666235659',
        '33491415235',
        '73464264123',
        '43421627523',
        '75794512473',
        '42980903903',
        '34163909975',
        '15399493122',
        '59287667773',
        '68055959153',
        '62090501223',
        '66524322223',
        '76893481783',
        '45411298403',
        '18677495634',
        '71661562933',
        '29237734451',
        '76692373813',
        '6405425293',
        '10323834490',
    ],
    '85931779': [
        '3357980466',
        '6005982631',
        '5465521703',
        '4359046215',
        '2431242394',
        '8498764809',
        '4145330605',
        '3697048443',
        '4145352759',
        '7658681101',
        '38354745903',
        '6146050289',
        '4585677607',
        '14606205711',
        '28759024777',
        '12681380596',
        '7793033027',
        '4359062327',
        '5797828682',
        '17103063218',
        '20194240041',
        '2519268292',
        '73488281073',
        '71221000433',
        '17361350662',
        '4491041761',
        '33727866891',
        '34693444953',
        '33678610493',
        '21755427386',
        '8503817935',
        '10199832510',
        '13238089469',
        '18286348638',
        '26542287481',
        '4816294705',
        '10686034264',
        '6231961487',
        '14714082600',
        '15234045036',
        '18739158272',
    ],
    '85940195': [
        '19752922291',
        '46258437153',
        '17720870809',
        '30806170182',
        '9592683007',
        '32948164917',
        '14931612726',
        '17401090910',
        '45561814693',
        '59565438653',
        '31273670939',
        '18033795052',
        '18158636870',
        '71943704193',
        '17103947364',
        '79181849003',
    ],
    '85933669': [
        '72377247903',
        '14876470413',
        '14336304536',
        '69843776883',
        '38260138253',
        '71442963373',
        '69574454733',
        '64952324733',
        '10812466135',
        '29019447061',
        '72762052813',
        '59887638373',
        '28753480235',
        '37095160093',
        '59621065333',
        '8633865943',
        '30809595996',
        '26942209475',
        '28931642703',
        '7522774367',
        '14876762443',
        '32592921499',
        '6850442761',
        '31487650383',
        '78726876333',
    ],
    '85936429': [
        '11385217525',
        '23216223880',
        '17201190333',
        '66737232233',
        '72523514653',
        '20005086168',
        '16801912819',
        '19822566411',
        '18453603282',
        '79742012893',
        '12616594244',
        '29624007567',
        '5152278241',
        '18457296958',
        '23372120571',
        '17857192427',
        '8455489991',
        '54911920463',
        '37083962253',
        '68728886903',
        '64637486183',
        '32717169187',
        '8449372466',
        '27236875801',
        '30479930378',
        '16801911812',
    ],
    '101735835': [
        '8970007578',
        '69126541133',
        '80356423873',
        '26371888983',
        '37290961173',
        '14225797363',
        '79709265253',
        '44439714593',
        '45363637093',
        '77388563263',
        '74346899623',
        '29118394881',
        '36302943123',
        '80356413803',
        '9735220836',
        '19931955814',
        '36375940553',
    ],
    '85922583': [
        '17443813892',
        '7538884353',
        '35978517933',
        '42670133633',
        '19822355948',
        '2431242394',
        '8498764809',
        '4145330605',
        '3697048443',
        '4145352759',
        '7658681101',
        '33663831761',
        '7799747703',
        '16548919174',
        '31621744517',
        '6146050289',
        '33106030293',
        '7793033027',
        '5797828682',
        '12012514077',
        '49102557603',
        '113898749',
        '55211754713',
        '18468279300',
        '73740554713',
        '4491041761',
        '16549088350',
        '4099361055',
        '17116794670',
        '52196887343',
        '8503817935',
        '13205072960',
        '36429653933',
        '503315719',
        '22110327417',
        '77597878283',
        '17405142071',
        '7722426215',
        '18286348638',
        '30673401260',
        '78526362493',
        '4816294705',
        '25460703019',
        '12504980378',
        '34077481179',
    ],
    '101933229': [
        '16525367458',
        '36136828403',
        '32585854373',
        '34999583023',
        '26943564897',
        '60537576313',
        '63900644143',
        '74204419193',
        '64210689373',
        '32854274251',
        '32854576351',
        '41001282883',
        '17586238937',
        '46904316883',
        '32732052647',
        '32045649223',
        '61932402223',
        '36771862743',
        '27302256283',
        '26539751855',
        '20048756737',
        '61294840313',
        '29124269719',
        '26238173481',
        '3846622181',
    ],
};
